/**
 * useBatchExecution — shared hook for fan-out stage components.
 *
 * Handles running a stage once per item (single or batch), tracking
 * per-item status, cancel support, and refreshing document/run afterward.
 */

import { useCallback, useRef, useState } from 'react';
import { useArticleContext } from '../ArticleContext';
import { jobsService } from '../../../services/jobsService';

export type BatchItemStatus = { status: string; error?: string };
export type BatchStatus = Record<string, BatchItemStatus>;

export interface UseBatchExecutionOptions {
  stageId: string;
  /** Called before the first item in a batch run (e.g., resetFromStage). */
  onBeforeBatch?: () => Promise<void>;
  /** Called before a single-item run (e.g., resetFromStage). */
  onBeforeSingle?: () => Promise<void>;
}

export function useBatchExecution({ stageId, onBeforeBatch, onBeforeSingle }: UseBatchExecutionOptions) {
  const { run, refreshDocument, refreshRun, refreshArticles } = useArticleContext();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [runningAll, setRunningAll] = useState(false);
  const [batchStatus, setBatchStatus] = useState<BatchStatus>({});
  const [runningSingle, setRunningSingle] = useState(false);
  const cancelRef = useRef(false);

  const runSingle = useCallback(async (itemId: string) => {
    if (!run?.run_id) return;
    setRunningSingle(true);
    try {
      if (onBeforeSingle) await onBeforeSingle();
      const { job_id } = await jobsService.submit('blog_stage_execute', {
        run_id: run.run_id,
        stage_id: stageId,
        extraction_id: itemId,
      });
      await jobsService.waitForCompletion(job_id);
      await refreshRun();
      await refreshDocument();
      await refreshArticles();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error(`${stageId} single run failed:`, msg);
    } finally {
      setRunningSingle(false);
    }
  }, [run?.run_id, stageId, onBeforeSingle, refreshRun, refreshDocument, refreshArticles]);

  const runAll = useCallback(async (itemIds: string[]) => {
    if (!run?.run_id) return;
    cancelRef.current = false;
    setRunningAll(true);
    setBatchStatus({});
    try {
      if (onBeforeBatch) await onBeforeBatch();
    } catch (e: unknown) {
      setRunningAll(false);
      return;
    }
    for (const id of itemIds) {
      if (cancelRef.current) break;
      setBatchStatus((prev) => ({ ...prev, [id]: { status: 'running' } }));
      try {
        const { job_id } = await jobsService.submit('blog_stage_execute', {
          run_id: run.run_id,
          stage_id: stageId,
          extraction_id: id,
        });
        await jobsService.waitForCompletion(job_id);
        setBatchStatus((prev) => ({ ...prev, [id]: { status: 'succeeded' } }));
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        setBatchStatus((prev) => ({ ...prev, [id]: { status: 'failed', error: msg } }));
      }
    }
    setRunningAll(false);
    await refreshRun();
    await refreshDocument();
    await refreshArticles();
  }, [run?.run_id, stageId, onBeforeBatch, refreshRun, refreshDocument, refreshArticles]);

  const cancelBatch = useCallback(() => {
    cancelRef.current = true;
  }, []);

  return {
    selectedId,
    setSelectedId,
    runningAll,
    batchStatus,
    runningSingle,
    runSingle,
    runAll,
    cancelBatch,
  };
}
